import { cachedFetch } from './cachedFetch.js';
const DEFAULT_TEAM_SEASON = 2026;
const FALLBACK_AVAILABLE_SEASONS = [2026, 2025, 2024, 2023, 2022, 2021];
const TEAM_RESPONSE_CACHE = new Map();
const CURRENT_TEAM_CACHE_MS = 45_000;
const HISTORICAL_TEAM_CACHE_MS = 5 * 60_000;

export async function getTeams({
  season = DEFAULT_TEAM_SEASON,
  signal,
  force = false,
} = {}) {
  const normalizedSeason = Number(season);

  if (!Number.isInteger(normalizedSeason)) {
    throw new Error('A valid team season is required.');
  }

  const cached = TEAM_RESPONSE_CACHE.get(normalizedSeason);
  const cacheTtl = normalizedSeason === DEFAULT_TEAM_SEASON
    ? CURRENT_TEAM_CACHE_MS
    : HISTORICAL_TEAM_CACHE_MS;

  if (!force && cached && Date.now() - cached.storedAt < cacheTtl) {
    return cached.value;
  }

  const params = new URLSearchParams({ season: String(normalizedSeason) });
  const response = await cachedFetch(`/api/teams?${params.toString()}`, {
    signal,
    force,
    maxAge: cacheTtl,
    staleAge: cacheTtl,
    headers: { Accept: 'application/json' },
    cache: normalizedSeason === 2026 || force ? 'no-cache' : 'default',
  });

  let payload = null;

  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  if (!response.ok) {
    throw new Error(
      payload?.message ||
        (response.status >= 500
          ? 'The team directory is temporarily unavailable.'
          : 'Unable to load the requested team season.')
    );
  }

  if (!payload || !Array.isArray(payload.teams)) {
    throw new Error('The team directory returned an invalid response.');
  }

  const returnedSeason = integerOrNull(payload.season);

  if (returnedSeason === null) {
    throw new Error('The team directory did not identify its season.');
  }

  const availableSeasons = normalizeAvailableSeasons(
    payload.availableSeasons,
    returnedSeason
  );

  const teams = payload.teams
    .filter(
      (team) =>
        team &&
        typeof team === 'object' &&
        typeof team.name === 'string' &&
        team.name.trim()
    )
    .map((team) => normalizeTeam(team, returnedSeason));

  const normalizedPayload = {
    ...payload,
    season: returnedSeason,
    availableSeasons,

    rankingsSeason: integerOrNull(payload.rankingsSeason),
    rankingsAvailable: Boolean(payload.rankingsAvailable),
    archivedRankingsSeason:
      integerOrNull(payload.archivedRankingsSeason) ?? returnedSeason,
    archivedRankingsAvailable: Boolean(payload.archivedRankingsAvailable),
    finalRankingsAvailable: Boolean(
      payload.finalRankingsAvailable ?? payload.archivedRankingsAvailable
    ),
    recordsAvailable: Boolean(payload.recordsAvailable),
    seasonFinishesAvailable: Boolean(payload.seasonFinishesAvailable),
    archiveComplete: Boolean(payload.archiveComplete),
    upstreamPartial: Boolean(payload.upstreamPartial),

    rankingsOfficial: Boolean(payload.rankingsOfficial),
    rankingsLabel: stringValue(payload.rankingsLabel),
    rankingsArchiveType: stringValue(payload.rankingsArchiveType),
    rankingsUpdated: stringValue(payload.rankingsUpdated),
    recordsUpdated: stringValue(payload.recordsUpdated),
    finishesUpdated: stringValue(payload.finishesUpdated),
    rankingCoverage: normalizeRankingCoverage(payload.rankingCoverage),
    recordCoverage: normalizeRecordCoverage(payload.recordCoverage),
    finishCoverage: normalizeFinishCoverage(payload.finishCoverage),
    archiveSummary: normalizeArchiveSummary(payload.archiveSummary),
    dataStatus: normalizeDataStatus(payload.dataStatus, returnedSeason),
    teams,
  };

  TEAM_RESPONSE_CACHE.set(normalizedSeason, {
    value: normalizedPayload,
    storedAt: Date.now(),
  });

  return normalizedPayload;
}

function normalizeAvailableSeasons(value, returnedSeason) {
  const seasons = Array.isArray(value)
    ? value.map(integerOrNull).filter((season) => season !== null)
    : [];

  return Array.from(
    new Set([...seasons, returnedSeason, ...FALLBACK_AVAILABLE_SEASONS])
  ).sort((left, right) => right - left);
}

function normalizeTeam(team, returnedSeason) {
  const rankingHistory = normalizeRankingHistory(team.rankingHistory);
  const rankingSnapshot =
    normalizeRankingObservation(team.rankingSnapshot) ||
    rankingHistory.at(-1) ||
    null;
  const finalRecord = normalizeFinalRecord(team.finalRecord);
  const seasonFinish = normalizeSeasonFinish(
    team.seasonFinish,
    returnedSeason
  );

  const legacyRank = integerOrNull(team.rank);
  const archivedRank =
    integerOrNull(team.archivedRank) ??
    integerOrNull(team.finalRank) ??
    rankingSnapshot?.rank ??
    legacyRank;
  const archivedRankLabel =
    stringValue(team.archivedRankLabel) ||
    stringValue(team.finalRankLabel) ||
    rankingSnapshot?.rankLabel ||
    (archivedRank !== null ? `#${archivedRank}` : '');

  const recordWins = finalRecord?.wins ?? integerOrNull(team.wins);
  const recordLosses = finalRecord?.losses ?? integerOrNull(team.losses);
  const recordLabel =
    finalRecord?.label ||
    stringValue(team.record) ||
    (recordWins !== null && recordLosses !== null
      ? `${recordWins}-${recordLosses}`
      : '');
  const winPct =
    finiteNumberOrNull(team.winPct) ??
    calculateWinPct(recordWins, recordLosses);

  return {
    ...team,
    slug: stringValue(team.slug),
    name: stringValue(team.name),
    longName: stringValue(team.longName),
    season: integerOrNull(team.season) ?? returnedSeason,
    aliases: Array.isArray(team.aliases)
      ? team.aliases.map(stringValue).filter(Boolean)
      : [],
    canonicalId: stringValue(team.canonicalId),
    conference: stringValue(team.conference),

    record: recordLabel,
    wins: recordWins,
    losses: recordLosses,
    winPct,
    winPctLabel: stringValue(team.winPctLabel) || formatWinPct(winPct),
    winPctRank: integerOrNull(team.winPctRank),

    // Existing official-poll compatibility fields.
    rank: legacyRank,
    rankLabel: stringValue(team.rankLabel),
    tied: Boolean(team.tied),
    firstPlaceVotes: integerOrNull(team.firstPlaceVotes),
    rankingPoints: integerOrNull(team.rankingPoints),
    previousRank: stringValue(team.previousRank),
    rankingRecord: stringValue(team.rankingRecord),
    rankingAvailable: Boolean(team.rankingAvailable),

    // Archive-first final ranking fields.
    archivedRank,
    archivedRankLabel,
    archivedRankTied: Boolean(
      team.archivedRankTied ?? team.finalRankTied ?? rankingSnapshot?.tied
    ),
    archivedRankingAvailable: Boolean(
      team.archivedRankingAvailable ?? archivedRank !== null
    ),
    finalRank: archivedRank,
    finalRankLabel: archivedRankLabel,
    finalRankTied: Boolean(
      team.finalRankTied ?? team.archivedRankTied ?? rankingSnapshot?.tied
    ),
    finalRankAvailable: Boolean(
      team.finalRankAvailable ?? archivedRank !== null
    ),
    rankingOfficial: Boolean(team.rankingOfficial),
    rankingContextSeason:
      integerOrNull(team.rankingContextSeason) ?? returnedSeason,
    rankingContextLabel: stringValue(team.rankingContextLabel),
    rankingSourceType: stringValue(team.rankingSourceType),
    rankingSourceLabel: stringValue(team.rankingSourceLabel),
    rankingObservedDate: stringValue(team.rankingObservedDate),
    rankingGameId: stringValue(team.rankingGameId),
    rankingGameUrl: stringValue(team.rankingGameUrl),
    rankingSourceUrl: stringValue(team.rankingSourceUrl),
    rankingOpponentName: stringValue(team.rankingOpponentName),
    rankingOpponentSlug: stringValue(team.rankingOpponentSlug),
    rankingConfidence: stringValue(team.rankingConfidence),
    rankingArchiveType: stringValue(team.rankingArchiveType),
    rankingHistory,
    rankingSnapshot,

    finalRecord,
    finalRecordAvailable: Boolean(
      team.finalRecordAvailable ?? finalRecord?.label
    ),
    seasonFinish,
    seasonFinishAvailable: Boolean(
      team.seasonFinishAvailable ?? seasonFinish?.display ?? seasonFinish?.label
    ),
    seasonFinishCode:
      stringValue(team.seasonFinishCode) || seasonFinish?.code || '',
    seasonFinishLabel:
      stringValue(team.seasonFinishLabel) || seasonFinish?.label || '',
    seasonFinishDisplay:
      stringValue(team.seasonFinishDisplay) ||
      seasonFinish?.display ||
      seasonFinish?.label ||
      '',
    tournamentTeam: Boolean(
      team.tournamentTeam ?? seasonFinish?.tournamentTeam
    ),

    logo: stringValue(team.logo),
  };
}

function normalizeDataStatus(value, season) {
  const source = value && typeof value === 'object' ? value : {};
  return {
    mode: stringValue(source.mode) || (season === 2026 ? 'in-season' : 'final'),
    primary: stringValue(source.primary),
    secondary: stringValue(source.secondary),
    rankingSeason: integerOrNull(source.rankingSeason),
  };
}

function normalizeFinalRecord(value) {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const wins = integerOrNull(value.wins);
  const losses = integerOrNull(value.losses);
  const ties = integerOrNull(value.ties) ?? 0;
  const matches =
    integerOrNull(value.matches) ??
    (wins !== null && losses !== null ? wins + losses + ties : null);
  const label =
    stringValue(value.label) ||
    (wins !== null && losses !== null
      ? `${wins}-${losses}${ties ? `-${ties}` : ''}`
      : '');

  if (!label && wins === null && losses === null) {
    return null;
  }

  return {
    wins,
    losses,
    ties,
    matches,
    label,
    throughDate: stringValue(value.throughDate),
    sourceType: stringValue(value.sourceType),
    complete: Boolean(value.complete),
  };
}

function normalizeSeasonFinish(value, returnedSeason) {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const label = stringValue(value.label);
  const placement = stringValue(value.placement);
  const display =
    stringValue(value.display) ||
    (label && placement ? `${label} — ${placement}` : label);

  if (!display && !label) {
    return null;
  }

  return {
    code: stringValue(value.code),
    label,
    placement,
    display,
    roundReached: stringValue(value.roundReached),
    tournamentTeam: Boolean(value.tournamentTeam),
    result: stringValue(value.result),
    tournamentWins: integerOrNull(value.tournamentWins),
    lastGameId: stringValue(value.lastGameId),
    lastGameDate: stringValue(value.lastGameDate),
    opponentName: stringValue(value.opponentName),
    sourceType: stringValue(value.sourceType),
    sourceUrl: stringValue(value.sourceUrl),
    season: integerOrNull(value.season) ?? returnedSeason,
    verified: Boolean(value.verified),
  };
}

function normalizeRankingCoverage(value) {
  const coverage = value && typeof value === 'object' ? value : {};
  const dateRange =
    coverage.dateRange && typeof coverage.dateRange === 'object'
      ? coverage.dateRange
      : {};

  return {
    official: Boolean(coverage.official),
    archiveType: stringValue(coverage.archiveType),
    label: stringValue(coverage.label),
    teamsWithRankings: integerOrNull(coverage.teamsWithRankings) ?? 0,
    observations: integerOrNull(coverage.observations) ?? 0,
    dateRange: {
      start: stringValue(dateRange.start),
      end: stringValue(dateRange.end),
    },
    note: stringValue(coverage.note),
  };
}

function normalizeRecordCoverage(value) {
  const coverage = value && typeof value === 'object' ? value : {};
  const dateRange =
    coverage.dateRange && typeof coverage.dateRange === 'object'
      ? coverage.dateRange
      : {};

  return {
    teamsWithRecords: integerOrNull(coverage.teamsWithRecords) ?? 0,
    teamsWithVerifiedPublishedRecords:
      integerOrNull(coverage.teamsWithVerifiedPublishedRecords) ?? 0,
    teamsWithOfficialCompletedMatches:
      integerOrNull(coverage.teamsWithOfficialCompletedMatches) ?? 0,
    teamsWithoutOfficialCompletedMatches:
      integerOrNull(coverage.teamsWithoutOfficialCompletedMatches) ?? 0,
    complete: Boolean(coverage.complete),
    verified: Boolean(coverage.verified),
    source: stringValue(coverage.source),
    dateRange: {
      start: stringValue(dateRange.start),
      end: stringValue(dateRange.end),
    },
    note: stringValue(coverage.note),
  };
}

function normalizeFinishCoverage(value) {
  const coverage = value && typeof value === 'object' ? value : {};

  return {
    teamsWithFinishes: integerOrNull(coverage.teamsWithFinishes) ?? 0,
    tournamentTeams: integerOrNull(coverage.tournamentTeams) ?? 0,
    complete: Boolean(coverage.complete),
    bracketGames: integerOrNull(coverage.bracketGames),
    bracketTeams: integerOrNull(coverage.bracketTeams),
    note: stringValue(coverage.note),
  };
}

function normalizeArchiveSummary(value) {
  const summary = value && typeof value === 'object' ? value : {};

  return {
    teams: integerOrNull(summary.teams),
    teamsWithRankings: integerOrNull(summary.teamsWithRankings),
    teamsWithFinalRecords: integerOrNull(summary.teamsWithFinalRecords),
    tournamentTeams: integerOrNull(summary.tournamentTeams),
    observations: integerOrNull(summary.observations),
    bracketFetched: Boolean(summary.bracketFetched),
    bracketComplete: Boolean(summary.bracketComplete),
    bracketGamesParsed: integerOrNull(summary.bracketGamesParsed),
    bracketTeamsParsed: integerOrNull(summary.bracketTeamsParsed),
    bracketRecoveredGames: integerOrNull(summary.bracketRecoveredGames),
    completeRecordCoverage: Boolean(summary.completeRecordCoverage),
    completeRankingScan: Boolean(summary.completeRankingScan),
  };
}

function normalizeRankingHistory(value) {
  if (!Array.isArray(value)) {
    return [];
  }

  const seen = new Set();

  return value
    .map(normalizeRankingObservation)
    .filter(Boolean)
    .filter((entry) => {
      const key = [
        entry.recordedDate,
        entry.rank,
        entry.gameId,
        entry.sourceType,
      ].join('|');

      if (seen.has(key)) {
        return false;
      }

      seen.add(key);
      return true;
    })
    .sort(compareRankingObservations);
}

function normalizeRankingObservation(value) {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const rank = integerOrNull(value.rank);

  if (rank === null || rank < 1) {
    return null;
  }

  const rankLabel = stringValue(value.rankLabel) || `#${rank}`;

  return {
    rank,
    rankLabel,
    tied: Boolean(value.tied) || /^t-/i.test(rankLabel),
    recordedDate: stringValue(value.recordedDate),
    gameId: stringValue(value.gameId),
    gameUrl: stringValue(value.gameUrl),
    sourceUrl: stringValue(value.sourceUrl),
    opponentName: stringValue(value.opponentName),
    opponentSlug: stringValue(value.opponentSlug),
    sourceType: stringValue(value.sourceType),
    confidence: stringValue(value.confidence),
    record: stringValue(value.record),
    points: integerOrNull(value.points),
    previousRank: stringValue(value.previousRank),
    firstPlaceVotes: integerOrNull(value.firstPlaceVotes),
  };
}

function compareRankingObservations(left, right) {
  return (
    left.recordedDate.localeCompare(right.recordedDate) ||
    String(left.gameId).localeCompare(String(right.gameId)) ||
    left.rank - right.rank
  );
}

function calculateWinPct(wins, losses) {
  if (!Number.isInteger(wins) || !Number.isInteger(losses)) {
    return null;
  }

  const matches = wins + losses;
  return matches > 0 ? wins / matches : 0;
}

function formatWinPct(value) {
  if (!Number.isFinite(value)) {
    return '';
  }

  return value.toFixed(3).replace(/^0/, '');
}

function stringValue(value) {
  return typeof value === 'string' ? value.trim() : '';
}

function integerOrNull(value) {
  if (value === null || value === undefined || String(value).trim() === '') {
    return null;
  }

  const number = Number(value);
  return Number.isInteger(number) ? number : null;
}

function finiteNumberOrNull(value) {
  if (value === null || value === undefined || String(value).trim() === '') {
    return null;
  }

  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}
